class ChatSession {

  $interval;
  $rootScope;
  API;
  BOT_EVENTS;
  LocalStorage;
  USER_TYPES;
  Utils;

  #SESSION_MAX_AGE;
  #SESSION_REFRESH_INTERVAL;
  #CHECK_OFFICE_OPEN_INTERVAL;
  #KEY_USER_DIRECT_ACCESS;
  #BOT_MENU_KEY_BACK_TO_START;
  BOT_MENU_KEY_CLOSE_TICKET;
  #PATTERN_PROD_CODE;
  #REGEX_PROD_CODE;

  #session;
  #analyseActive;
  #suggestedQuestionPool;

  #sessionRefreshIntervalPromise;
  #officeOpenRefreshIntervalPromise;

  static #MAX_RELOADS_WITHOUT_BOT_RESPONSE = 2;
  #reloadsWithoutBotResponse;


  static $inject = ['$interval', '$rootScope', 'API', 'BOT_EVENTS', 'LocalStorage', 'USER_TYPES', 'Utils'];

  constructor($interval, $rootScope, API, BOT_EVENTS, LocalStorage, USER_TYPES, Utils) {
    this.$interval = $interval;
    this.$rootScope = $rootScope;
    this.API = API;
    this.BOT_EVENTS = BOT_EVENTS;
    this.LocalStorage = LocalStorage;
    this.USER_TYPES = USER_TYPES;
    this.Utils = Utils;


    this.#SESSION_MAX_AGE = 1000 * 60; // 1 minute
    this.#SESSION_REFRESH_INTERVAL = 3500; // @NOTE this is checks the this.#session. The *server* refresh interval is triggered here from the bot, every 10 seconds
    this.#CHECK_OFFICE_OPEN_INTERVAL = 1000 * 60;
    this.#KEY_USER_DIRECT_ACCESS = 'user_sda';
    this.#BOT_MENU_KEY_BACK_TO_START = 'key_back_to_start';
    this.BOT_MENU_KEY_CLOSE_TICKET = 'key_close_ticket';
    this.#PATTERN_PROD_CODE = '{{ product_code }}';
    this.#REGEX_PROD_CODE = new RegExp(this.#PATTERN_PROD_CODE, 'g');

    this.#session = {
      conversation_key: null,
      panelOpen: false,
      bubbleDismissed: false,
      activeTicket: false,
      transcript: [],
      menu: null,
      menuOnlyMode: false,
      userStatus: {},
      teamStatus: {},
      errorMessage: null,
      suggestedQuestionsAsked: [],
      isStalled: false,
    };

    this.#analyseActive = false;


    this.#suggestedQuestionPool = {
      backToOptions: {
        singleUse: false,
        button: "Start again",
        content: "Start again",
        isStartAgain: true,
        key: this.#BOT_MENU_KEY_BACK_TO_START
      },
      onStalled: {
        singleUse: false,
        button: "Show menu",
        content: "[Show menu]",
        key: this.#BOT_MENU_KEY_BACK_TO_START
      },
      requestSupportLive: {
        singleUse: false,
        button: "Talk to the team",
        content: "Talk to the team",
      },
      requestSupportOffline: {
        singleUse: false,
        button: "Open support ticket",
        content: "Open support ticket"
      },
      closeSupportTicket: {
        singleUse: false,
        button: "Close support ticket",
        content: "Close support ticket",
        key: this.BOT_MENU_KEY_CLOSE_TICKET,
      },
      askAboutProduct: {
        singleUse: true,
        button: "Ask about " + this.#PATTERN_PROD_CODE,
        content: "Tell me more about " + this.#PATTERN_PROD_CODE
      }
    };


    this.#sessionRefreshIntervalPromise = null;
    this.#officeOpenRefreshIntervalPromise = null;

    this.#reloadsWithoutBotResponse = 0;

  }


  init(didReset) {

    didReset = didReset || false;
    this.#session = this.#readSession(this.#session);
    this.#reloadsWithoutBotResponse = 0;

    this.API.get("/2/users/self/status")
      .then(response => {

        if (!response.success) {
          this.#session.errorMessage = "An unknown error occurred at get user status";
          return;
        }

        this.#session.userStatus = response.status;
        this.#session.teamStatus = response.team_status;

        this.#writeSession(this.#session);
        this.$rootScope.$broadcast(this.BOT_EVENTS.chatSessionReady, null);

        if (this.#session.conversation_key) {
          refreshFromServer(didReset);
        }

        if (!this.#sessionRefreshIntervalPromise) {
          this.#sessionRefreshIntervalPromise = this.$interval(() => {
            this.#session = this.#readSession(this.#session);
          }, this.#SESSION_REFRESH_INTERVAL);
        }

        if (!this.#officeOpenRefreshIntervalPromise) {
          this.#officeOpenRefreshIntervalPromise = this.$interval(() => {
            // do nothing if the panel is open
            // we only need to separately check if support is live when the panel is closed
            // this is because when the panel is open the this.#session refreshes regularly, and the sesssion payload contains the live support status
            if (this.#session && this.#session.panelOpen) {
              return;
            }
            this.refreshOfficeOpenStatusFromServer();
          }, this.#CHECK_OFFICE_OPEN_INTERVAL);
        }

      }, response => {
        this.#session.errorMessage = response;
      });

  } // end init


  get onStalledSuggestion(){
    return this.#suggestedQuestionPool.onStalled;
  }




  dismissBubble() {
    this.#session.bubbleDismissed = true;
    this.#writeSession(this.#session);
  }




  /**
   * 
   * @param {?string} conversationKeyOrNull - if passing a key we activate an existing chat (realistically this will be a support ticket), otherwise we reset to a new blank chat
   */
  reset(conversationKeyOrNull) {
    const resetWithKey = conversationKeyOrNull ? true : false;
    const session = this.#session;
    session.conversation_key = conversationKeyOrNull || null;
    session.panelOpen = resetWithKey;
    session.activeTicket = resetWithKey;
    session.transcript = [];
    session.menu = null;
    session.menuOnlyMode = false;
    session.userStatus = {};
    session.teamStatus = {};
    session.errorMessage = null;
    session.suggestedQuestionsAsked = [];
    session.isStalled = false;
    this.#writeSession(session);
    this.init(resetWithKey);
  }




  setErrorMessage(message) {
    this.#session.errorMessage = message || null;
  }

  setKey(conversation_key) {
    this.#session.conversation_key = conversation_key;
  }

  togglePanel(flag) {
    this.#session.panelOpen = flag;
    this.#writeSession(this.#session);
  }

  #transcriptContainsComment(content) {
    return this.#session.transcript.filter(comment => comment.content === content).length;
  }

  getSuggestions(botContext) {

    const suggestions = [];
    const addSuggestionToList = (suggestion, suggestions, suggestedQuestionsAsked) => {
      if (!suggestion.singleUse || suggestedQuestionsAsked.indexOf(suggestion.content) === -1) {
        suggestions.push(suggestion);
      }
    }

    // other than the above case, only show suggestions when the last comment was from the BOT
    // this prevents the suggestions from reappearing as soon as the user has typed someting and before the bot has responded
    let lastComment = (this.#session.transcript[this.#session.transcript.length - 1]);
    const questionsAsked = this.#session.suggestedQuestionsAsked;
    if (!lastComment) {
      return [];
    }
    if (this.#session.activeTicket) {
      addSuggestionToList(this.#suggestedQuestionPool.closeSupportTicket, suggestions, questionsAsked);
      return suggestions;
    }
    if (lastComment.userType === "customer") {
      return [];
    }

    // it user has support direct access
    if (this.#session.userStatus && this.#session.userStatus[this.#KEY_USER_DIRECT_ACCESS]) {
      let questionToUse = (this.#session.teamStatus && this.#session.teamStatus.support_team_online) ? this.#suggestedQuestionPool.requestSupportLive : this.#suggestedQuestionPool.requestSupportOffline;
      addSuggestionToList(questionToUse, suggestions, questionsAsked);
    }

    // @TODO add more suggestions here (e.g. based on user status OR what page they are on)
    if (botContext.page_type === "product" && botContext.product_id && botContext.product_code) {
      let suggestion = angular.copy(this.#suggestedQuestionPool.askAboutProduct);
      suggestion.button = suggestion.button.replace(this.#REGEX_PROD_CODE, botContext.product_code);
      suggestion.content = suggestion.content.replace(this.#REGEX_PROD_CODE, botContext.product_code);
      if (!this.#transcriptContainsComment(suggestion.content)) {
        addSuggestionToList(suggestion, suggestions, questionsAsked);
      }
    }

    if (!this.#session.menu) {
      suggestions.push(this.#suggestedQuestionPool.backToOptions);
    }
    return suggestions;
  }





  useSuggestion(sugestionContent) {
    this.#session.suggestedQuestionsAsked.push(sugestionContent);
    this.#writeSession(this.#session);
  }


  refreshFromServer(didReset) {

    if (!this.#session || !this.#session.conversation_key) {
      return;
    }
    didReset = didReset || false;

    this.API.get("/2/bot/chat/session/" + this.#session.conversation_key)
      .then(response => {
        if (!response.success) {
          this.#session.errorMessage = "An unknown error occurred at get this.#session";
          return;
        }

        // in the case that the user had an active support this.#session (with WRP staff)
        // then they logged out, the this.#session will return a special state to tell the app to reset the this.#session (get a new key)
        if (response.doInvalidate) {
          this.$rootScope.$broadcast(this.BOT_EVENTS.doReset, null);
        } else {
          // unlikely because we don't delete them but still needs to be handled
          if (!response.transcript || !response.transcript.length) {
            console.warn("Chat transcript is empty, resetting");
            this.$rootScope.$broadcast(this.BOT_EVENTS.doReset, null);
            return;
          }
        }

        const existingTranscriptLength = this.#session.transcript.length ?? 0;
        this.#session.activeTicket = response.active_ticket;
        this.#session.supportTicketId = response.support_ticket_id;
        this.#session.transcript = this.#processTranscript(response.transcript) || [];
        this.#writeSession(this.#session);

        if (response.menu) {
          this.setMenu(response.menu, response.menuOnlyMode || false, false);
          this.#session.isStalled = false; // never stalled when the user has a menu to click
          this.#reloadsWithoutBotResponse = 0;
        } else {
          this.clearMenu();
          if(!this.#session.activeTicket){
            this.#session.isStalled = this.#getUserIsWaiting(response.lastCommentUser);  
          } else {
            this.#session.isStalled = false;
          }
        }

        if (response.script) {
          this.setScript(response.script);
        } else {
          this.clearScript();
        }

        this.$rootScope.$broadcast(this.BOT_EVENTS.chatSessionRefreshed, {
          fromServer: true,
          didReset: didReset
        });
        this.$rootScope.$broadcast(this.BOT_EVENTS.menuStatusEvent, { enabled: true });

        // if the transcript got longer then scroll to the bottom so they can see the new reply in view
        if (this.#session.transcript.length > existingTranscriptLength) {
          this.$rootScope.$broadcast(this.BOT_EVENTS.scrollToEnd);
        }
        this.#session.teamStatus = response.team_status;

      }, response => {
        this.#session.errorMessage = response;
      });
  }




  refreshOfficeOpenStatusFromServer() {
    this.API.get("/2/support/status")
      .then(response => {
        if (!response.success) {
          return console.warn("Could not get support status");
        }
        if (!this.#session) {
          return console.warn("Session not yet loaded at getLiveSupportStatusFromServer")
        }

        this.#session.teamStatus = response.team_status;
        this.#writeSession(this.#session);
      }, response => {
        console.warn("Get live status failed with response: ", response);
      });
  }


  /**
 * 
 * @param {object} commentObject 
 */
  addComment(commentObject) {
    if (commentObject && commentObject.content) {
      commentObject.content = this.Utils.replaceUrls(commentObject.content);
    }
    this.#session.transcript.push(commentObject);
    this.#writeSession(this.#session);
    this.$rootScope.$broadcast(this.BOT_EVENTS.chatSessionRefreshed, null);
  }



  /**
   * 
   * @param {array} menu 
   * @param {bool} menuOnlyMode 
   */
  setMenu(menu, menuOnlyMode, broadcastEvents) {
    this.#session.menu = angular.copy(menu);
    this.#session.menuOnlyMode = menuOnlyMode || false;
    this.#writeSession(this.#session);
    if (broadcastEvents) {
      this.$rootScope.$broadcast(this.BOT_EVENTS.menuStatusEvent, { enabled: true });
      this.$rootScope.$broadcast(this.BOT_EVENTS.scrollToEnd);
    }
  }

  clearMenuOnUse() {
    this.#session.menu = null;
    this.#writeSession(this.#session);
  }

  clearMenu() {
    this.#session.menu = null;
    this.#session.menuOnlyMode = false;
    this.#writeSession(this.#session);
    // $rootScope.$broadcast(this.BOT_EVENTS.chatSessionRefreshed, null); // not needed here?
  }

  setScript(script) {
    this.#session.script = script;
    this.#writeSession(this.#session);
  }

  clearScript() {
    delete this.#session.script;
    this.#writeSession(this.#session);
  }

  #writeSession(_session) {
    const sessionToWrite = angular.copy(_session);
    if (sessionToWrite.hasOwnProperty("errorMessage")) {
      delete sessionToWrite.errorMessage;
    }
    this.LocalStorage.write("supportSession", sessionToWrite);
  }


  #readSession(existingSession) {

    // @NOTE only properties of the existing this.#session will be updated from the browser session storage
    let sessionFromStorage = this.LocalStorage.read("supportSession", this.#SESSION_MAX_AGE);
    if (!sessionFromStorage) {
      this.#writeSession(existingSession);
    } else {
      for (let property in existingSession) {
        if (sessionFromStorage.hasOwnProperty(property)) {
          existingSession[property] = sessionFromStorage[property];
        }
      }
    }
    this.$rootScope.$broadcast(this.BOT_EVENTS.chatSessionRefreshed, null);
    return existingSession;
  }


  #processTranscript(transcript) {
    if (!transcript || !transcript.length) {
      return [];
    }

    transcript.forEach(comment => {
      if (comment.metadata && comment.metadata.includes("youtube_key")) {
        let metadata = null;
        try {
          metadata = JSON.parse(comment.metadata);
          comment.youtube_key = metadata.youtube_key || null;
        } catch (error) {
          console.warn("Unable to read comment metadata: ", error);
        }
      }
      comment.content = this.Utils.replaceUrls(comment.content);
    });
    return transcript;
  }

  #getUserIsWaiting(lastCommentUser) {
    this.#reloadsWithoutBotResponse++;
    if (lastCommentUser === this.USER_TYPES.USER_TYPE_CUSTOMER) {
      if (this.#reloadsWithoutBotResponse >= ChatSession.#MAX_RELOADS_WITHOUT_BOT_RESPONSE) {
        return true;
      }
      return false;
    } else {
      this.#reloadsWithoutBotResponse = 0;
      return false;
    }
  }


  get session() {
    return this.#session;
  }

  get showTyping() {
    return this.#analyseActive && !this.#session.activeTicket;
  }

  /**
   * @param {boolean} flag
   */
  setAnalyse(flag) {
    this.#analyseActive = flag;
  }


}


angular.module('bot').service('ChatSession', ChatSession);
